<div class="header-content d-flex w-100 align-items-center h-100 px-3">
  <!-- Section 1: Brand -->
  <div class="d-flex align-items-center me-4">
    <a
      class="navbar-brand text-decoration-none d-flex align-items-center gap-2"
      (click)="goHome()"
      style="cursor: pointer"
    >
      <i class="bi bi-grid-3x3-gap-fill text-primary fs-4"></i>
      <span class="text-white fw-bold fs-5 tracking-tight">OnPlan</span>
    </a>

    <!-- Tools Menu (home / folder pages only, internal users only) -->
    <div ngbDropdown class="d-inline-block ms-3" *ngIf="isHomePage && !isExternalUser">
      <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
        Tools
      </button>
      <div ngbDropdownMenu class="shadow mt-1">
        <button ngbDropdownItem (click)="openCombinedPlanogramsPdfModal()">
          <i class="bi bi-file-earmark-pdf me-2 text-muted"></i>Combined Planograms PDF
        </button>
      </div>
    </div>
  </div>

  <!-- Section 2: Main Toolbar -->
  <div
    class="d-flex align-items-center justify-content-end flex-grow-1 gap-2"
    *ngIf="isLayoutVisible(); else layoutPlaceholder"
  >
    <!-- Toolbar Menus -->
    <div class="d-flex gap-1">
      <!-- Linked (view-only) Indicator -->
      <span
        *ngIf="isLinkedPlanogram"
        class="badge bg-info text-dark d-flex align-items-center gap-2 me-2"
        title="This is a linked planogram. It is view-only — open the reference planogram to edit it. You can still export it."
      >
        <i class="bi bi-link-45deg"></i>
        <span>View only — Linked</span>
      </span>
      <!-- Problems Indicator -->
      <button
        *ngIf="planogramProblemsService.hasProblems$ | async"
        class="btn btn-sm btn-danger d-flex align-items-center gap-2 me-2"
        (click)="openProblemsModal()"
      >
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>Problems</span>
      </button>
      <!-- History Group -->
      <div
        class="btn-group me-2"
        role="group"
        *hasCapability="CapabilityType.EDIT_PLANOGRAM"
      >
        <button
          type="button"
          class="btn btn-sm btn-dark border-secondary"
          [disabled]="!canUndo()"
          (click)="undo()"
          ngbTooltip="Undo"
          placement="bottom"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-dark border-secondary"
          [disabled]="!canRedo()"
          (click)="redo()"
          ngbTooltip="Redo"
          placement="bottom"
        >
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <!-- File Menu -->
      <div
        ngbDropdown
        class="d-inline-block"
        *hasCapability="[
          CapabilityType.EXPORT,
          CapabilityType.IMPORT_PRODUCTS,
          CapabilityType.REMOVE_PRODUCTS,
          CapabilityType.EDIT_NOTES,
          CapabilityType.REFRESH_PLANOGRAM,
        ]"
      >
        <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
          File
        </button>
        <div ngbDropdownMenu class="shadow mt-1">
          <button
            ngbDropdownItem
            (click)="openPlanogramRefreshModal()"
            *hasCapability="CapabilityType.REFRESH_PLANOGRAM"
          >
            <i class="bi bi-cloud-arrow-up me-2 text-muted"></i>Refresh Planogram
          </button>
          <button
            ngbDropdownItem
            (click)="openExportPlanogramModal()"
            *hasCapability="CapabilityType.EXPORT"
          >
            <i class="bi bi-cloud-arrow-up me-2 text-muted"></i>Export
          </button>
          <button
            ngbDropdownItem
            (click)="openImportPlanogramModal()"
            *hasCapability="CapabilityType.IMPORT_PRODUCTS"
          >
            <i class="bi bi-box-arrow-in-down me-2 text-muted"></i>Import
            Products
          </button>
          <div class="dropdown-divider"></div>
          <button
            ngbDropdownItem
            (click)="openRemoveProductsModal()"
            *hasCapability="CapabilityType.REMOVE_PRODUCTS"
          >
            <i class="bi bi-trash me-2 text-muted"></i>Remove Products
          </button>
          <button
            ngbDropdownItem
            (click)="openNotesModal()"
            *hasCapability="CapabilityType.EDIT_NOTES"
          >
            <i class="bi bi-sticky me-2 text-muted"></i>Notes
          </button>
        </div>
      </div>

      <!-- Images Menu -->
      <div
        ngbDropdown
        class="d-inline-block"
        *hasCapability="CapabilityType.IMAGES_EDITOR"
      >
        <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
          Images
        </button>
        <div ngbDropdownMenu class="shadow mt-1">
          <button ngbDropdownItem (click)="openImagesManager()">
            <i class="bi bi-card-image me-2 text-muted"></i>Products
          </button>
          <button ngbDropdownItem (click)="openLogosManager()">
            <i class="bi bi-card-heading me-2 text-muted"></i>Logos
          </button>
        </div>
      </div>

      <!-- View Menu -->
      <div ngbDropdown class="d-inline-block">
        <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
          View
        </button>
        <div ngbDropdownMenu class="shadow mt-1">
          <button ngbDropdownItem (click)="openSelectReportModal()">
            <i class="bi bi-clipboard-data me-2 text-muted"></i>Reports
          </button>
          <div class="dropdown-divider"></div>
          <button ngbDropdownItem (click)="toggleAnalysisOverlay()">
            <i class="bi bi-bar-chart me-2 text-muted"></i>Analysis Overlay
          </button>
          <button ngbDropdownItem (click)="openDataBreakdown()">
            <i class="bi bi-table me-2 text-muted"></i>Data Breakdown
          </button>
        </div>
      </div>

      <!-- Automation menu -->
      <div
        ngbDropdown
        class="d-inline-block"
        *hasCapability="CapabilityType.GENERATE_PLANOGRAM"
      >
        <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
          Automation
        </button>
        <div ngbDropdownMenu class="shadow mt-1">
          <button
            ngbDropdownItem
            [disabled]="!canGeneratePlanogram()"
            (click)="generatePlanogram()"
          >
            <i class="bi bi-magic me-2 text-muted"></i>Generate
          </button>
        </div>
      </div>

      <!-- Planogram menu -->
      <div ngbDropdown class="d-inline-block" *hasCapability="[
        CapabilityType.SAVE_PLANOGRAM, 
        CapabilityType.SAVENEW_PLANOGRAM, 
        CapabilityType.LOAD_PLANOGRAM, 
        CapabilityType.RESET_PLANOGRAM,
        CapabilityType.EDIT_OPTIONS
        ]">
        <button class="btn btn-sm btn-dark-toolbar" ngbDropdownToggle>
          Planogram
        </button>
        <div ngbDropdownMenu class="shadow mt-1">
          <button
            ngbDropdownItem
            (click)="savePlanogram()"
            *hasCapability="CapabilityType.SAVE_PLANOGRAM"
          >
            <i class="bi bi-save me-2 text-muted"></i>Save
          </button>
          <button
            ngbDropdownItem
            (click)="openPlanogramSaveModal()"
            *hasCapability="CapabilityType.SAVENEW_PLANOGRAM"
          >
            <i class="bi bi-save2 me-2 text-muted"></i>Save As New
          </button>
          <button
            ngbDropdownItem
            (click)="openPlanogramLoadModal()"
            *hasCapability="CapabilityType.LOAD_PLANOGRAM"
          >
            <i class="bi bi-folder2-open me-2 text-muted"></i>Load
          </button>
          <div class="dropdown-divider"></div>
          <button
            ngbDropdownItem
            (click)="resetPlanogram()"
            *hasCapability="CapabilityType.RESET_PLANOGRAM"
            class="text-danger"
          >
            <i class="bi bi-arrow-counterclockwise me-2"></i>Reset Planogram
          </button>
          <div
            class="dropdown-divider"
            *hasCapability="CapabilityType.EDIT_OPTIONS"
          ></div>
          <button
            ngbDropdownItem
            (click)="openPlanogramOptions()"
            *hasCapability="CapabilityType.EDIT_OPTIONS"
          >
            <i class="bi bi-gear me-2 text-muted"></i>Options
          </button>
        </div>
      </div>

      <!-- Collaboration -->
      <div class="d-flex gap-1">
        <button
          class="btn btn-sm btn-dark-toolbar btn-icon"
          *hasCapability="CapabilityType.SHARE_PLANOGRAM"
          (click)="openSharePlanogramModal()"
          ngbTooltip="Share"
        >
          <i class="bi bi-share"></i>
        </button>
        <button
          class="btn btn-sm btn-dark-toolbar btn-icon"
          *hasCapability="CapabilityType.FEEDBACK"
          (click)="openPlanogramFeedbackModal()"
          ngbTooltip="Feedback"
        >
          <i class="bi bi-chat-dots"></i>
        </button>
        <div class="vr bg-secondary mx-1 opacity-25"></div>
      </div>

      <div class="vr bg-secondary mx-2 opacity-25"></div>

      <!-- Zoom Control -->
      <div
        class="d-xxl-flex align-items-center gap-2 px-2 text-white-50"
      >
        <i class="bi bi-zoom-out fs-5 zoom-icon" (click)="zoomOut()"></i>
        <input
          [(ngModel)]="planogramZoom"
          (ngModelChange)="setZoom($event)"
          type="range"
          class="form-range custom-range"
          style="width: 7vw"
          [min]="ZoomService.MIN_ZOOM"
          [max]="ZoomService.MAX_ZOOM"
          step="0.1"
        />
        <i class="bi bi-zoom-in fs-5 zoom-icon" (click)="zoomIn()"></i>
      </div>
    </div>
  </div>
  <ng-template #layoutPlaceholder>
    <div class="flex-grow-1"></div>
  </ng-template>

  <!-- Section 3: Right Actions (System & User) -->
  <div class="d-flex align-items-center gap-2">
    <!-- User Profile & Global Nav -->
    <ng-container *ngIf="authService.authorized | async">
      <button
        class="btn btn-dark-toolbar btn-icon"
        (click)="gotoActivityCenter()"
        *hasCapability="CapabilityType.SHARING_CENTER"
        ngbTooltip="Sharing Center"
      >
        <i class="bi bi-compass"></i>
      </button>

      <!-- Notifications -->
      <div class="d-flex align-items-center">
        <app-notifications></app-notifications>
      </div>

      <!-- User Dropdown -->
      <div ngbDropdown class="d-inline-block">
        <button class="btn btn-dark-toolbar btn-icon" ngbDropdownToggle>
          <i class="bi bi-person-circle fs-6"></i>
        </button>
        <div ngbDropdownMenu class="dropdown-menu-end shadow mt-2">
          <h6 class="dropdown-header">User Account</h6>
          <button ngbDropdownItem (click)="logout()">
            <i class="bi bi-box-arrow-right me-2 text-muted"></i>Logout
          </button>
        </div>
      </div>
    </ng-container>
  </div>
</div>
